import React, { useMemo } from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PuntuacionConfirmada from '@/components/alineaciones/shared/complementos/PuntuacionConfirmada';
import TierPuntuacionConfirmada from '@/components/alineaciones/shared/complementos/TierPuntuacionConfirmada';
import FantasyIconSelector from '@/components/shared/icons/FantasyIconSelector';
import CustomEquipoWithTooltip from '@/DesignSystem/components/partidos/CustomEquipoWithTooltip';

const FantasyStatsSection = ({ title, homePlayers, awayPlayers, mode, fixture, sectionId }) => {
    const theme = useTheme();

    // Ordenar jugadores por puntuación descendente
    const sortedHomePlayers = useMemo(() => {
        return [...(homePlayers || [])].sort((a, b) => {
            const scoreA = parseFloat(a.score || 0);
            const scoreB = parseFloat(b.score || 0);
            return scoreB - scoreA;
        });
    }, [homePlayers]);

    const sortedAwayPlayers = useMemo(() => {
        return [...(awayPlayers || [])].sort((a, b) => {
            const scoreA = parseFloat(a.score || 0);
            const scoreB = parseFloat(b.score || 0);
            return scoreB - scoreA;
        });
    }, [awayPlayers]);

    const hasData = sortedHomePlayers.length > 0 || sortedAwayPlayers.length > 0;

    if (!hasData) {
        return null;
    }

    const maxLength = Math.max(sortedHomePlayers.length, sortedAwayPlayers.length);

    // Generar ID para scroll - usar el prop si existe, sino generarlo desde el title
    const finalSectionId = useMemo(() => {
        if (sectionId) {
            return sectionId;
        }
        return title
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/\s+/g, '-')
            .replace(/[^a-z0-9-]/g, '');
    }, [sectionId, title]);

    return (
        <Box
            id={finalSectionId}
            sx={{
                marginBottom: 4,
                scrollMarginTop: '100px',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <FantasyIconSelector fantasy={mode} size={20} />
                <Typography variant="h2" fontSize={20} fontWeight="normal">
                    {title}
                </Typography>
            </Box>

            {/* Headers de equipos */}
            <Grid container spacing={2} sx={{ mb: 1 }}>
                {/* Equipo Local */}
                <Grid size={6}>
                    {fixture?.home && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                padding: '8px',
                                backgroundColor: 'background.default',
                                borderBottom: '2px solid',
                                borderColor: theme.palette.primary.main,
                            }}
                        >
                            <CustomEquipoWithTooltip
                                teamId={fixture.home.id}
                                alt={fixture.home.name}
                                width={28}
                                height={28}
                                label={fixture.home.name}
                            />
                            <Typography
                                variant="body1"
                                sx={{
                                    fontSize: '16px',
                                    fontWeight: 'medium',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {fixture.home.name}
                            </Typography>
                        </Box>
                    )}
                </Grid>

                {/* Equipo Visitante */}
                <Grid size={6}>
                    {fixture?.away && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                padding: '8px',
                                backgroundColor: 'background.default',
                                borderBottom: '2px solid',
                                borderColor: theme.palette.primary.main,
                                flexDirection: 'row-reverse',
                            }}
                        >
                            <CustomEquipoWithTooltip
                                teamId={fixture.away.id}
                                alt={fixture.away.name}
                                width={28}
                                height={28}
                                label={fixture.away.name}
                            />
                            <Typography
                                variant="body1"
                                sx={{
                                    fontSize: '16px',
                                    fontWeight: 'medium',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    textAlign: 'right',
                                }}
                            >
                                {fixture.away.name}
                            </Typography>
                        </Box>
                    )}
                </Grid>
            </Grid>

            {Array.from({ length: maxLength }).map((_, index) => {
                const homePlayer = sortedHomePlayers[index];
                const awayPlayer = sortedAwayPlayers[index];

                return (
                    <Grid container spacing={2} key={`row-${index}`} sx={{ marginBottom: '8px' }}>
                        {/* Equipo Local - Columna Izquierda */}
                        <Grid size={6}>
                            {homePlayer ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '8px',
                                        backgroundColor: 'background.default',
                                        gap: 1,
                                        borderBottom: '1px solid',
                                        borderColor: theme.palette.border?.main,
                                    }}
                                >
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                fontSize: '14px',
                                                fontWeight: 'medium',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {homePlayer.name || 'Jugador desconocido'}
                                        </Typography>
                                        <TierPuntuacionConfirmada
                                            estadisticaPartido={homePlayer.score}
                                            confirmedMode={mode}
                                            fontSize={11}
                                        />
                                    </Box>
                                    <PuntuacionConfirmada
                                        estadisticaPartido={homePlayer.score}
                                        fontSize={12}
                                        width="35px"
                                        height="20px"
                                        confirmedMode={mode}
                                    />
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        padding: '8px',
                                        backgroundColor: 'background.default',
                                        borderRadius: '6px',
                                        minHeight: '56px',
                                    }}
                                />
                            )}
                        </Grid>

                        {/* Equipo Visitante - Columna Derecha */}
                        <Grid size={6}>
                            {awayPlayer ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '8px',
                                        backgroundColor: 'background.default',
                                        gap: 1,
                                        flexDirection: 'row-reverse',
                                        borderBottom: '1px solid',
                                        borderColor: theme.palette.border?.main,
                                    }}
                                >
                                    <Box sx={{ flex: 1, minWidth: 0, textAlign: 'right' }}>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                fontSize: '14px',
                                                fontWeight: 'medium',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {awayPlayer.name || 'Jugador desconocido'}
                                        </Typography>
                                        <Box sx={{ textAlign: 'right' }}>
                                            <TierPuntuacionConfirmada
                                                estadisticaPartido={awayPlayer.score}
                                                confirmedMode={mode}
                                                fontSize={11}
                                            />
                                        </Box>
                                    </Box>
                                    <PuntuacionConfirmada
                                        estadisticaPartido={awayPlayer.score}
                                        fontSize={12}
                                        width="35px"
                                        height="20px"
                                        confirmedMode={mode}
                                    />
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        padding: '8px',
                                        backgroundColor: 'background.default',
                                        borderRadius: '6px',
                                        minHeight: '56px',
                                    }}
                                />
                            )}
                        </Grid>
                    </Grid>
                );
            })}
        </Box>
    );
};

export default FantasyStatsSection;
